import { useCallback, useRef } from "react";
import { ClientResourceGroupFragment } from "@graphql/client-resource-groups/ClientResourceGroups.g";
import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import { useUpdateClientApplicationMutation } from "@graphql/client-applications/ClientApplications.g";

import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { ClientApplicationApprovalReviewForm, ClientApplicationApprovalReviewFormValues } from "@components/Forms/ClientApplicationApprovalReviewForm/ClientApplicationApprovalReviewForm";

type Props = {
  resourceGroup: ClientResourceGroupFragment | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const ClientResourceGroupReviewPanel = ({
  resourceGroup,
  clientId,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const updateClientApplicationMutation = useUpdateClientApplicationMutation();
  const formRef = useRef<HTMLFormElement>(null);

  const handleFormSubmit = useCallback(
    async (values: ClientApplicationApprovalReviewFormValues) => {
      const primaryApp = resourceGroup?.primaryResource?.primaryApplication?.edges?.[0]?.node;
      if (!primaryApp?.id) return;

      try {
        await updateClientApplicationMutation.mutateAsync({
          input: {
            id: primaryApp.id,
            clientId: values.clientId,
            reviewState: values.reviewState,
            managedBy: values.managedBy,
            supportedBy: values.supportedBy,
            businessOwnerId: values.businessOwnerId,
          },
        });
        onClose();
        onSuccess?.();
      } catch (error) {
        console.error("Failed to update client application:", error);
      }
    },
    [resourceGroup, updateClientApplicationMutation, onClose, onSuccess],
  );

  const handleApply = useCallback(() => {
    // Trigger form submission by dispatching a submit event
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  const primaryApp = resourceGroup?.primaryResource?.primaryApplication?.edges?.[0]?.node;

  if (!resourceGroup || !primaryApp) {
    return null;
  }

  return (
    <SlideInPanel
      isOpen={isOpen}
      onClose={onClose}
      title={`Review: ${resourceGroup.primaryResource?.productName || "Application"}`}
      onApply={handleApply}
      applyButtonText="Update Application"
    >
      <ClientApplicationApprovalReviewForm
        ref={formRef}
        values={{
          reviewState: primaryApp.reviewState ?? TenantedClientApplicationReviewStateChoices.Discovered,
          managedBy: primaryApp.managedBy ?? undefined,
          supportedBy: primaryApp.supportedBy ?? undefined,
          businessOwnerId: primaryApp.businessOwner?.id ?? undefined,
          clientId: clientId,
        }}
        onSubmit={handleFormSubmit}
        withSubmitButton={false}
      />
    </SlideInPanel>
  );
};